import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { apiGet } from './api';
import type { CmsLegal } from '@shared';

// Holds the cmsLegal disclaimer strings fetched from /api/cms/legal so the
// footer + contact page + any future surface read from one place. If the
// API is unreachable we fall back to a baseline disclaimer rather than
// breaking the page.

const FALLBACK: CmsLegal = {
  fca_perimeter_notice:
    'Omega Trade and Finance Ltd is not authorised or regulated by the Financial Conduct Authority (FCA). Services are strictly B2B and limited to body corporates that self-certify as sophisticated.',
  invoice_descriptor:
    'Fees invoiced as consultancy / research / introduction fees only.',
  contact_eligibility_body_corp:
    'I confirm we are a body corporate. Limited Company, PLC or LLP — not a sole trader, partnership of fewer than 4, or individual.',
  contact_eligibility_sophisticated:
    'I confirm sophisticated / HNW corporate status under the Financial Promotion Order.',
  contact_eligibility_not_advice:
    'I understand Omega is not FCA-authorised. Any advice or arranging will be performed by an authorised counterparty.',
  payment_descriptor:
    'Consultancy / research / introduction fees only. Omega Trade does not hold client money or arrange regulated investments. Payments go directly to Omega Trade and Finance Ltd via Revolut Business or by bank transfer to our operating account.',
  payment_failure_disclaimer:
    'Your bank did not authorise this payment. No funds have moved from your account. Please retry, use a different card, or pay by bank transfer using the reference shown on your invoice.',
  // KYC / GDPR regulatory slots (portal-kyc Phase F). Empty in the offline
  // fallback; real DRAFT values arrive from GET /api/cms/legal.
  kyc_processing_notice: '',
  kyc_consent_v1: '',
  data_retention_policy: '',
  cross_border_transfer_notice: '',
  version: '0.0.0-fallback',
  effective_at: '1970-01-01',
};

const LegalCtx = createContext<CmsLegal>(FALLBACK);

export function LegalProvider({ children }: { children: ReactNode }) {
  const [legal, setLegal] = useState<CmsLegal>(FALLBACK);
  useEffect(() => {
    apiGet<{ legal: CmsLegal }>('/api/cms/legal')
      .then((r) => setLegal(r.legal))
      .catch(() => { /* keep fallback */ });
  }, []);
  return <LegalCtx.Provider value={legal}>{children}</LegalCtx.Provider>;
}

export function useLegal(): CmsLegal {
  return useContext(LegalCtx);
}
